import { SetStateAction, SyntheticEvent, useState } from "react"
import { Box, Paper, Tab, Tabs, } from "@mui/material"
import { IMeal } from "../types/types";
import { MealItem } from "./MealItem";
import { useMeals } from "../hooks/useMeals";
import { Spinner } from "./spinner/Spinner";
import { LeadingActions, SwipeableList, SwipeableListItem, SwipeAction, TrailingActions, Type } from "react-swipeable-list";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import 'react-swipeable-list/dist/styles.css';
import { ref, remove } from "firebase/database";
import { realtimeDb } from "../utils/firebase";
import { useSnackbar } from "./Snackbar";
import EditIcon from '@mui/icons-material/Edit';
import { MealEdit } from "./MealEdit";
import AddIcon from '@mui/icons-material/Add';
import { FloatingButton } from "react-draggable-float-btn";

const CustomTabPanel = ({ meals, value, index, setCart, cart, setSnackbarStatus }: {
  meals: IMeal[],
  value: number,
  index: number,
  setCart: (value: SetStateAction<IMeal[]>) => void,
  setSnackbarStatus: (value: "idle" | "working" | "done" | "error") => void,
  cart: IMeal[]
}) => {
  const [editingMeal, setEditingMeal] = useState<IMeal>();
  const [addNewMeal, setAddNewMeal] = useState(false);

  const handleDelete = async (id: string) => {
    setSnackbarStatus("working");
    try {
      await remove(ref(realtimeDb, `meals/${id}`));
      setSnackbarStatus("done");
    } catch {
      setSnackbarStatus("error");
      console.error(`Nie udało się usunąć posiłku: ${id}`);
    } finally {
      setTimeout(() => {
        setSnackbarStatus("idle");
      }, 2000)
    }
  };

  const leadingAction = (meal: IMeal) => (
    <LeadingActions>
      <SwipeAction
        onClick={() => setEditingMeal(meal)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#1f8038",
            padding: "0 16px",
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          }}
        >
          <EditIcon style={{ color: "white" }} />
        </div>
      </SwipeAction>
    </LeadingActions>
  );

  const trailingActions = (id: string) => (
    <TrailingActions>
      <SwipeAction
        destructive={true}
        onClick={() => handleDelete(id)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#E66B55",
            padding: "0 16px",
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
          }}
        >
          <DeleteForeverIcon style={{ color: "white" }} />
        </div>
      </SwipeAction>
    </TrailingActions>
  );

  const mealsList = (meals: IMeal[]) => meals.map((meal) => {
    const existedMeal = cart.find((cartItem) => cartItem.name === meal.name)?.quantity
    return (
      <SwipeableListItem
        key={meal.name}
        trailingActions={trailingActions(meal.id!)}
        leadingActions={leadingAction(meal)}
        maxSwipe={0.2}
        className="shadow-sm rounded-lg"
      >
        <MealItem meal={{ ...meal, quantity: existedMeal || meal.quantity }} setCart={setCart} />
      </SwipeableListItem>
    )
  })

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{ paddingTop: 48 }}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <SwipeableList type={Type.IOS} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {index === 2 ? (
              <>
                <p style={{ marginBottom: 10 }}>Kolacje</p>
                  {mealsList(meals.filter((meal) => !(meal.name.toLowerCase()).includes('dzieci')))}
                <p style={{ margin: "10px 0" }}>Kolacje Dzieci</p>
                  {mealsList(meals.filter((meal) => (meal.name.toLowerCase()).includes('dzieci')))}
              </>
            ) : mealsList(meals)}
          </SwipeableList>
        </Box>
      )}
      <FloatingButton position={{ y: 665, x: 325 }} backgroundColor="#1f8038" color="#fff" onClick={() => setAddNewMeal(true)}><AddIcon /></FloatingButton>
      <MealEdit
        isOpen={!!editingMeal}
        handleClose={() => setEditingMeal(undefined)}
        handleOpen={() => setEditingMeal(editingMeal)}
        meal={editingMeal}
      />
      <MealEdit
        isOpen={addNewMeal}
        handleClose={() => setAddNewMeal(false)}
        handleOpen={() => setAddNewMeal(true)}
      />
    </div>
  );
}

export const MealTabs = ({ setCart, cart }: { setCart: (value: SetStateAction<IMeal[]>) => void, cart: IMeal[] }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { meals, loading, error } = useMeals();
  const { Snackbar, setSnackbarStatus } = useSnackbar();

  const handleTabChange = (_event: SyntheticEvent<Element, Event>, value: number) => setActiveTab(value);

  if (loading && meals.length === 0) {
    return <Spinner />
  }

  if (error) {
    return null
  }

  const breakfast = meals.filter((meal) => meal.type === "breakfast");
  const lunch = meals.filter((meal) => meal.type === "lunch");
  const dinner = meals.filter((meal) => meal.type === "dinner");
  const snack = meals.filter((meal) => meal.type === "snack");

  return (
    <div>
      <Paper sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1 }} elevation={3}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', background: "white" }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons={false}>
            <Tab label="Śniadania" />
            <Tab label="Obiady" />
            <Tab label="Kolacje" />
            <Tab label="Przekąski" />
          </Tabs>
        </Box>
      </Paper>
      <CustomTabPanel value={activeTab} index={0} meals={breakfast} setCart={setCart} cart={cart} setSnackbarStatus={setSnackbarStatus} />
      <CustomTabPanel value={activeTab} index={1} meals={lunch} setCart={setCart} cart={cart} setSnackbarStatus={setSnackbarStatus} />
      <CustomTabPanel value={activeTab} index={2} meals={dinner} setCart={setCart} cart={cart} setSnackbarStatus={setSnackbarStatus} />
      <CustomTabPanel value={activeTab} index={3} meals={snack} setCart={setCart} cart={cart} setSnackbarStatus={setSnackbarStatus} />
      <Snackbar messages={{ done: "Posiłek usunięty", working: "Usuwanie posiłku" }} />
    </div>
  )
}
